import { useRef } from 'react'

import Backdrop from '@/components/ui/Backdrop'
import Icon from '@/components/ui/Icon'
import { TONES } from '@/components/ui/tones'
import { useStageScale } from '@/hooks/useStageScale'
import { lerp } from '@/lib/scroll'

// Drawn on a 640×520 stage and scaled to fit.
const STAGE = 640

// The first "seen" avatar sits on top of the next (the data has three).
const AVATAR_Z = ['z-3', 'z-2', 'z-1']

// The round actions beside the screen: chat, attach, people.
const ACTIONS = [
  ['message', 'bg-brand shadow-[0_18px_30px_-14px_rgba(26,115,232,.6)]'],
  ['paperclip', 'bg-[#0f8b97] shadow-[0_18px_30px_-14px_rgba(15,139,151,.6)]'],
  ['users', 'bg-[#7c4dcc] shadow-[0_18px_30px_-14px_rgba(124,77,204,.6)]'],
]

/**
 * The messages screen (`art.screen`), tilted away at `t` = 0, turns to face
 * you and grows in front of the shared photo card, which drops back.
 * Decorative.
 */
export default function MessagingArt({ art, t }) {
  const { screen, photo, seen } = art
  const box = useRef(null)
  const scale = useStageScale(box, STAGE)

  return (
    <div ref={box} aria-hidden="true" className="relative aspect-[640/520] w-full">
      <div
        className="absolute top-0 left-0 h-[520px] w-[640px] origin-top-left text-[#1e2330]"
        style={{ transform: `scale(${scale})` }}
      >
        <div
          className={`absolute top-20 left-[70px] h-[330px] w-[460px] ${t > 0.35 ? 'z-5' : 'z-2'}`}
          style={{
            transform: `perspective(1800px) rotateX(${lerp(18, 6, t)}deg) rotateY(${lerp(-38, -10, t)}deg) rotateZ(${lerp(10, 2, t)}deg) scale(${lerp(0.84, 1.16, t)})`,
          }}
        >
          <img
            src={screen}
            alt=""
            loading="lazy"
            decoding="async"
            className="size-full rounded-[20px] shadow-[0_0_0_1px_#e3e7ee,0_50px_80px_-34px_rgba(12,26,51,.45)]"
          />
          <div className="absolute top-24 -right-[74px] flex flex-col gap-3">
            {ACTIONS.map(([icon, look]) => (
              <span
                key={icon}
                className={`grid size-[46px] place-items-center rounded-full text-white ${look}`}
              >
                <Icon name={icon} size={18} />
              </span>
            ))}
          </div>
        </div>

        <div
          className={`absolute top-[300px] left-7 w-[250px] ${t > 0.45 ? 'z-1' : 'z-4'}`}
          style={{
            transform: `translate(${lerp(0, -16, t)}px, ${lerp(0, 40, t)}px) scale(${lerp(1, 0.92, t)})`,
          }}
        >
          <div className="relative h-[170px] [transform:perspective(1600px)_rotateX(10deg)_rotateY(-14deg)_rotateZ(5deg)] overflow-hidden rounded-[22px] bg-ink shadow-[0_30px_50px_-24px_rgba(12,26,51,.45)]">
            <Backdrop overlay="bg-[linear-gradient(180deg,rgba(12,26,51,0)_45%,rgba(12,26,51,.75))]" />
            <span className="absolute start-4 bottom-3.5 flex flex-col gap-0.5 text-white">
              <span className="text-[13px] font-bold">{photo.title}</span>
              <span className="text-[11px] text-[#dbe4f3]">{photo.meta}</span>
            </span>
            <span className="absolute end-3.5 bottom-3.5 grid size-[34px] place-items-center rounded-full bg-white text-brand">
              <Icon name="message" size={14} stroke={2.2} />
            </span>
          </div>
        </div>

        <div className="absolute top-[30px] left-[390px] flex h-10 rotate-4 items-center gap-2 rounded-full bg-white ps-2 pe-3.5 shadow-[0_0_0_1px_#eef1f5,0_20px_40px_-20px_rgba(12,26,51,.35)]">
          <div className="flex">
            {seen.avatars.map((avatar, i) => (
              <span
                key={avatar.initials}
                className={`relative grid size-6 place-items-center rounded-full text-[9px] font-bold ring-2 ring-white ${TONES[avatar.tone]} ${AVATAR_Z[i]} ${i ? '-ms-1' : ''}`}
              >
                {avatar.initials}
              </span>
            ))}
          </div>
          <span className="text-xs font-bold whitespace-nowrap">{seen.label}</span>
          <Icon name="checks" size={15} stroke={2.2} className="text-brand" />
        </div>
      </div>
    </div>
  )
}
